// =============================================================================
// conversations/hooks/useAvailableChannels.ts — Query de
// `GET /api/conversations/canais-disponiveis` (F43-S10, contrato lido em
// apps/api/src/modules/conversations/channel-picker.schema.ts).
//
// Usado pela tela de "Iniciar conversa" (F43-S11) — o mesmo componente serve
// o CRM e o live chat, ambos consumindo este hook.
// =============================================================================

import { useQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api';
import type { AvailableChannelDto } from '../startConversationChoice';

interface AvailableChannelsResponse {
  readonly data: AvailableChannelDto[];
}

export const availableChannelsKeys = {
  byLead: (leadId: string) => ['conversations', 'canais-disponiveis', leadId] as const,
} as const;

/**
 * useAvailableChannels — caixas que o usuário autenticado pode usar para
 * iniciar uma conversa com `leadId`.
 *
 * Habilitado apenas quando `leadId` não é vazio. `staleTime` curto (10s):
 * sessão da caixa pessoal e janela de 24h mudam de estado com frequência —
 * diferente de templates aprovados, que mudam raramente.
 */
export function useAvailableChannels(leadId: string) {
  return useQuery({
    queryKey: availableChannelsKeys.byLead(leadId),
    queryFn: () =>
      api.get<AvailableChannelsResponse>(
        `/api/conversations/canais-disponiveis?leadId=${encodeURIComponent(leadId)}`,
      ),
    staleTime: 10_000,
    enabled: leadId.length > 0,
    select: (res) => res.data,
  });
}
